<template>
  <div class="mx-auto w-full max-w-lg text-center space-y-5 py-10">
    <LumiLoader v-if="!hardError && !finishedWithWarnings" size="page" class="mx-auto" />

    <!-- Wiping phase: erasing the demo before the real setup replays. -->
    <template v-if="mode === 'wiping'">
      <h1 class="text-xl font-bold text-title">{{ t('onboarding.commit.wiping.title') }}</h1>
      <p class="text-sm text-body">{{ t('onboarding.commit.wiping.body') }}</p>
      <div v-if="wipeFailed || stalled" class="space-y-3">
        <InlineNotice v-if="wipeFailed" variant="error">{{ t('onboarding.commit.wipeFailed') }}</InlineNotice>
        <p v-else class="text-sm text-body">{{ t('onboarding.commit.stalled') }}</p>
        <Button variant="secondary" :loading="busy" @click="retryWipe">{{ t('onboarding.commit.retry') }}</Button>
      </div>
    </template>

    <!-- Applying phase: the client replays the staged writes. -->
    <template v-else-if="mode === 'applying'">
      <h1 class="text-xl font-bold text-title">{{ t('onboarding.commit.applying.title') }}</h1>
      <p class="text-sm text-body">{{ t('onboarding.commit.applying.body') }}</p>

      <!-- Only an admin drives the replay (it performs admin-only writes). A
           non-admin who lands here just waits — the router routes non-admins away
           from the commit screen, so this is defense in depth. -->
      <template v-if="isAdmin">
        <ul class="text-left mx-auto inline-block space-y-1">
          <li v-for="s in replaySteps" :key="s.key" class="flex items-center gap-2 text-sm">
            <LumiIcon name="done" v-if="s.state === 'done'" class="h-4 w-4 text-success" />
            <Spinner v-else-if="s.state === 'running'" size="sm" />
            <span v-else-if="s.state === 'error'" class="h-4 w-4 text-danger-text">!</span>
            <span v-else-if="s.state === 'skipped'" class="h-4 w-4 text-center text-secondary">–</span>
            <span v-else class="h-4 w-4 rounded-full border border-strong" />
            <span :class="s.state === 'done' || s.state === 'skipped' ? 'text-secondary' : 'text-heading'">{{ s.label }}</span>
          </li>
        </ul>

        <div v-if="hardError" data-test="commit-error" class="space-y-3">
          <InlineNotice variant="error">{{ t('onboarding.commit.error') }}</InlineNotice>
          <!-- The seam's own reason for the failure. Until issue 195 this line did
               not exist: the axios interceptor put the detail on screen and the
               catch below only set the generic message. -->
          <InlineNotice v-if="replayError" variant="error" data-test="commit-error-detail">{{ replayError }}</InlineNotice>
          <div class="flex flex-wrap items-center justify-center gap-2">
            <Button variant="secondary" :loading="busy" @click="runReplay">{{ t('onboarding.commit.retry') }}</Button>
            <!-- The way out (issue 795): by now the demo is gone and the modal
                 cannot be closed, so a step that keeps failing must not hold the
                 whole workspace hostage. Every step is optional — the skipped one
                 can be set up later in the module's own settings. -->
            <Button v-if="failedKey" variant="secondary" :disabled="busy" data-test="commit-skip-step" @click="skipFailed">
              {{ t('onboarding.commit.skipStep') }}
            </Button>
            <Button variant="ghost" :disabled="busy" @click="emit('backToSetup', failedKey, replayError)">{{ t('onboarding.commit.backToSetup') }}</Button>
          </div>
        </div>
        <div v-else-if="stalled && !busy" class="space-y-3">
          <p class="text-sm text-body">{{ t('onboarding.commit.stalled') }}</p>
          <Button variant="secondary" @click="runReplay">{{ t('onboarding.commit.resume') }}</Button>
        </div>

        <!-- Setup resolved but with warnings worth reading: hold on this summary
             (instead of bouncing straight to the workspace) so they are actually
             seen. Both are non-blocking — the tenant is fully set up. -->
        <div v-if="finishedWithWarnings" class="space-y-3">
          <p v-if="skippedKeys.length" class="text-xs text-warning-text" data-test="commit-skipped">
            {{ t('onboarding.commit.skipped', { steps: skippedKeys.map(labelFor).join(', ') }) }}
          </p>
          <p v-if="logoMissing" class="text-xs text-warning-text">
            {{ t('onboarding.commit.logoMissing') }}
          </p>
          <p v-if="workflowFailed" class="text-xs text-warning-text" data-test="commit-workflow-failed">
            {{ t('onboarding.commit.workflowFailed') }}
          </p>
          <!-- #380: NOT a failure — the chain generated fine, one of its definitions
               just could not be made current yet because a council role it names has
               nobody assigned. Success tone, not the warning line above. -->
          <p v-if="workflowDraftedRoleKeys.length" class="text-xs text-success-text" data-test="commit-workflow-drafted">
            {{ t('onboarding.commit.workflowDrafted', { roles: workflowDraftedRoleKeys.map(workflowRoleName).join(', ') }) }}
          </p>
          <Button variant="primary" @click="goToWorkspace">{{ t('onboarding.commit.continue') }}</Button>
        </div>
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { Button, Spinner, LumiLoader, InlineNotice, LumiIcon } from '@lumi/ui';
import { clearWorkspaceRoleState, useApiMutation } from '@lumi/app-kit';
import { userApi } from '../../utils/api';
import { currentTenantSlug } from '../../utils/runtime-config';
import { useOnboardingStore, type OnboardingStatus } from '../../stores/onboarding';
import { useTenantsStore } from '../../stores/tenants';
import { REPLAY_ORDER, replayCompanyName, replayStep } from '../../utils/onboarding-replay';

const { t } = useI18n();
const router = useRouter();
const onboardingStore = useOnboardingStore();
const tenantsStore = useTenantsStore();
const { mode, status, isAdmin } = storeToRefs(onboardingStore);

// On an unrecoverable replay error, let the wizard show the (still-staged, editable)
// sections again so the admin can fix the input and finish again (issue 214). It
// is handed the step that failed and why, so it can open that step and say so
// (issue 795) — `'__company'` is the company name, which lives in the general step.
const emit = defineEmits<{ (e: 'backToSetup', failedKey: string | null, reason: string | null): void }>();

const busy = ref(false);
const hardError = ref(false);
// A picked job-ad logo was lost before it could be uploaded (reload / another admin
// resumed the commit): surfaced as a warning so the loss is not silent.
const logoMissing = ref(false);
// The hiring chain could not be generated AT ALL (issue 23) — a genuine failure
// (network/server error). The tenant stays on the lean default and setup still
// completes, so this is a warning with a retry route, not an error — the builder
// in ATS workflow settings re-runs the whole thing whenever they want.
const workflowFailed = ref(false);
// #380: the chain WAS generated, but one of its definitions could not be made
// current — a council role it names is unstaffed — so it was saved as a draft
// instead. Not a failure (`replayWorkflowBuilder` never throws for this, since
// #380 packet 1's `activate: 'ifStaffed'` never refuses): the role keys named,
// deduped across every drafted definition, for the commit screen to activate
// once the role has someone.
const workflowDraftedRoleKeys = ref<string[]>([]);
/** The three council role keys `workflowDraftedRoleKeys` can name (ats-api's
 * `COUNCIL_ROLE_KEYS`, `ats-api/src/workflow/council-staffing.ts`) — mirrors
 * `backoffice-ui`'s `roleName()` locally rather than sharing code (ADR 0001). */
const WORKFLOW_ROLE_KEYS = ['ats_works_council', 'ats_sbv', 'ats_legal'];
function workflowRoleName(roleKey: string): string {
  return WORKFLOW_ROLE_KEYS.includes(roleKey) ? t(`onboarding.commit.workflowRoles.${roleKey}`) : roleKey;
}
// Replay resolved but there are warnings the admin should read before leaving.
const finishedWithWarnings = ref(false);
// Per-step UI state, keyed like the replay: '__company' + staged sections.
const stepState = ref<Record<string, 'pending' | 'running' | 'done' | 'error' | 'skipped'>>({});
// The step the last replay failed on, and the steps the admin chose to finish
// without (issue 795). A skipped section is still marked applied server-side —
// that marker is all `finish` asks for — so it is skipped for good, not retried.
const failedKey = ref<string | null>(null);
const skippedKeys = ref<string[]>([]);

const stalled = computed(() => status.value?.demo?.stalled === true);
const wipeFailed = computed(() => mode.value === 'wiping' && !!status.value?.demo?.error);

function labelFor(key: string): string {
  if (key === '__company') return t('onboarding.commit.steps.company');
  if (key === 'general') return t('onboarding.wizard.general.title');
  return t(`onboarding.wizard.sections.${key}.title`);
}

// The steps to replay: company name (always) + every staged section, in order.
const replayKeys = computed<string[]>(() => [
  '__company',
  ...REPLAY_ORDER.filter((k) => onboardingStore.staged[k]),
]);
const replaySteps = computed(() =>
  replayKeys.value.map((key) => ({
    key,
    label: labelFor(key),
    state:
      stepState.value[key] ||
      (key !== '__company' && onboardingStore.applied[key] ? 'done' : 'pending'),
  })),
);

// Re-fires suite_wipe from a stale/failed wiping state (server-side guard).
// `inlineEdit` on the default `global` channel: success is visible as the screen
// moving on from 'wiping', and this phase has no form to render a failure into.
const { mutate: beginCommit } = useApiMutation<void, []>({
  tier: 'inlineEdit',
  run: async () => {
    const tenantId = await onboardingStore.activeTenantId();
    if (!tenantId) return;
    const res = await userApi.post(`${onboardingStore.base(tenantId)}/commit/begin`, { empty: false });
    onboardingStore.apply(res.data as OnboardingStatus);
  },
});

async function retryWipe() {
  if (busy.value) return;
  busy.value = true;
  try {
    await beginCommit();
  } finally {
    busy.value = false;
  }
}

// Back to the workspace, with its data re-read (#364 F8). This wizard is a MODAL
// over the workspace, so `router.replace('/')` only drops the `?setup=1` query:
// the view underneath is never remounted and every panel it fetched while the DEMO
// was live — module dashboards, the tenant's module list, the personnel-file card,
// the greeting — would survive the commit that just wiped that demo server-side.
// `refresh()` re-fetches the membership list and bumps the store's data stamp, which
// is what WorkspaceView (and the keyed cards) watch.
async function goToWorkspace() {
  await tenantsStore.refresh();
  router.replace('/');
}

// Replay every not-yet-applied staged section, then resolve. Idempotent + resumable:
// applied sections are skipped, and each write is guarded against duplicates. Only
// an admin may run it — the replay performs admin-only writes, so a non-admin would
// just 403 its way to a spurious error screen (the router keeps non-admins away, so
// this is defense in depth).
// The whole replay is ONE user action (Finish) with one outcome, so it is one seam
// call rather than one per write: the admin pressed a button once, and a failure
// anywhere in the chain leaves the same screen in the same state. `inlineEdit`
// because success is already visible — every step ticks green and the screen
// leaves — and `inPlace` because this screen renders the failure itself, as the
// step marker plus `replayError` under the generic line.
//
// The writes themselves live in `utils/onboarding-replay.ts` (a commit orchestrator,
// ADR 0032 — not a per-write wrapper), and the mutation-seam guard reads `inSeam`
// per file, so it cannot see this call covering them. That module is therefore on
// the guard's `SEAM_COVERED_FILES` map against THIS file, and each of its calls
// carries a `seam-covered:` marker naming it. The map is verified, not trusted: the
// guard checks that this file calls the seam and renders a failure. Keep
// `replayError` rendered — deleting that render silently un-covers ten writes, and
// the guard is what makes that a build failure rather than a discovery in support.
const {
  formError: replayError,
  mutate: replay,
} = useApiMutation<void, []>({
  tier: 'inlineEdit',
  errorChannel: 'inPlace',
  run: async () => {
    const tenantId = await onboardingStore.activeTenantId();
    if (!tenantId) return;
    // 0) Forget the role, delegation and persona this browser kept for the workspace
    //    (issue 795). The demo wipe erased the modules and re-provisioned them, so
    //    ATS's roles exist again under NEW ids — and the demo's `activeRoleId` still
    //    rides along on every write below, which ats-api answers with a 403
    //    (ACTIVE_ROLE_NOT_ASSIGNED). A fresh setup holds nothing worth keeping
    //    either: each module falls back to the admin's own role.
    clearWorkspaceRoleState(currentTenantSlug());

    // 1) Company name — always, both paths (from checkout tenants.name).
    if (skippedKeys.value.includes('__company')) {
      stepState.value['__company'] = 'skipped';
    } else {
      stepState.value['__company'] = 'running';
      // Prefer the name the admin set in the wizard (staged general) over the possibly
      // empty checkout name; replayGeneral re-applies it with the profile basics.
      await replayCompanyName(onboardingStore.staged['general']?.companyName || onboardingStore.companyName);
      stepState.value['__company'] = 'done';
    }

    // 2) Each staged section, in replay order; skip already-applied ones.
    for (const key of REPLAY_ORDER) {
      if (!onboardingStore.staged[key]) continue;
      if (onboardingStore.applied[key]) {
        stepState.value[key] = skippedKeys.value.includes(key) ? 'skipped' : 'done';
        continue;
      }
      const skipping = skippedKeys.value.includes(key);
      stepState.value[key] = 'running';
      if (!skipping) {
        const res = await replayStep(key, onboardingStore.staged[key], onboardingStore.stagedLogoFile);
        if (res.logoMissing) logoMissing.value = true;
        if (res.workflowFailed) workflowFailed.value = true;
        if (res.workflowDraftedRoleKeys?.length) workflowDraftedRoleKeys.value = res.workflowDraftedRoleKeys;
      }
      const applied = await userApi.post(
        `${onboardingStore.base(tenantId)}/commit/applied/${encodeURIComponent(key)}`,
      );
      onboardingStore.apply(applied.data as OnboardingStatus);
      stepState.value[key] = skipping ? 'skipped' : 'done';
    }

    // 3) Resolve, server-side.
    const finished = await userApi.post(`${onboardingStore.base(tenantId)}/finish`);
    onboardingStore.apply(finished.data as OnboardingStatus);
  },
  onError: () => {
    // Mark the running step failed. Retry resumes from here (applied sections +
    // created rows are skipped).
    failedKey.value = null;
    for (const k of Object.keys(stepState.value)) {
      if (stepState.value[k] === 'running') {
        stepState.value[k] = 'error';
        failedKey.value = k;
      }
    }
    hardError.value = true;
  },
});

// `busy` rather than the seam's `pending` because it also covers `retryWipe` and
// the post-success navigation tail below, which the seam call does not span.
async function runReplay() {
  if (busy.value || onboardingStore.mode !== 'applying' || !onboardingStore.isAdmin) return;
  busy.value = true;
  hardError.value = false;
  try {
    const outcome = await replay();
    if (!outcome.ok) return;
    // Hold on the summary if there are warnings the admin should read (a skipped
    // step, a lost logo, a hiring chain that could not be generated); otherwise go
    // straight home.
    // Neither blocks: setup is complete either way.
    onboardingStore.stagedLogoFile = null;
    if (skippedKeys.value.length || logoMissing.value || workflowFailed.value || workflowDraftedRoleKeys.value.length) {
      finishedWithWarnings.value = true;
    } else {
      await goToWorkspace();
    }
  } finally {
    busy.value = false;
  }
}

// "Finish without this step": leave the failed section out and carry on from there.
function skipFailed() {
  const key = failedKey.value;
  if (!key || busy.value) return;
  if (!skippedKeys.value.includes(key)) skippedKeys.value.push(key);
  void runReplay();
}

// Kick off replay as soon as we are in 'applying' (fresh path lands here on mount;
// the demo path flips wiping → applying via the store poll → this watcher).
watch(
  mode,
  (m) => {
    if (m === 'applying' && !busy.value && !hardError.value) void runReplay();
  },
  { immediate: false },
);

onMounted(() => {
  if (mode.value === 'applying') void runReplay();
});
</script>
